import { apiService } from './api';

export interface Category {
  id: string;
  value: string;
  label: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCategoryRequest {
  value: string;
  label: string;
  description?: string;
}

export interface UpdateCategoryRequest {
  label?: string;
  description?: string;
}

export interface GetCategoriesResponse {
  data: Category[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

class CategoryService {
  // Get all categories (for dropdowns)
  async getCategoriesForDropdown(): Promise<{ value: string; label: string }[]> {
    try {
      return await apiService.get<{ value: string; label: string }[]>('/categories/dropdown');
    } catch (error) {
      console.error('Error fetching categories for dropdown:', error);
      // Return default categories as fallback
      return [
        { value: 'ai_future_tech', label: 'AI & Future Tech' },
        { value: 'automobile', label: 'Automobile' },
        { value: 'business_entrepreneurship', label: 'Business & Entrepreneurship' },
        { value: 'casino', label: 'Casino' },
        { value: 'ecommerce_startups', label: 'E-commerce & Startups' },
        { value: 'education_career', label: 'Education & Career' },
        { value: 'entertainment_media', label: 'Entertainment & Media' },
        { value: 'environment', label: 'Environment' },
        { value: 'esports', label: 'Esports' },
        { value: 'family', label: 'Family' },
        { value: 'fashion_beauty', label: 'Fashion & Beauty' },
        { value: 'finance_investment', label: 'Finance & Investment' },
        { value: 'food_nutrition', label: 'Food & Nutrition' },
        { value: 'gaming', label: 'Gaming' },
        { value: 'general', label: 'General' },
        { value: 'health_fitness', label: 'Health & Fitness' },
        { value: 'home_improvement', label: 'Home Improvement' },
        { value: 'law', label: 'Law' },
        { value: 'lifestyle_wellness', label: 'Lifestyle & Wellness' },
        { value: 'magazine', label: 'Magazine' },
        { value: 'marketing_seo', label: 'Marketing & SEO' },
        { value: 'news', label: 'News' },
        { value: 'others', label: 'Others' },
        { value: 'parenting_relationships', label: 'Parenting & Relationships' },
        { value: 'real_estate', label: 'Real Estate' },
        { value: 'sports_fitness', label: 'Sports & Fitness' },
        { value: 'sustainability_green_living', label: 'Sustainability & Green Living' },
        { value: 'technology_gadgets', label: 'Technology & Gadgets' },
        { value: 'travel_tourism', label: 'Travel & Tourism' },
      ];
    }
  }

  // Get categories with pagination (for Settings page)
  async getCategories(params?: {
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<GetCategoriesResponse> {
    return await apiService.get<GetCategoriesResponse>('/categories', params);
  }

  // Create new category
  async createCategory(data: CreateCategoryRequest): Promise<Category> {
    return await apiService.post<Category>('/categories', data);
  }

  // Update category
  async updateCategory(id: string, data: UpdateCategoryRequest): Promise<Category> {
    return await apiService.put<Category>(`/categories/${id}`, data);
  }

  // Delete category
  async deleteCategory(id: string): Promise<void> {
    await apiService.delete(`/categories/${id}`);
  }

  // Rename category value (updates all references)
  async renameCategory(id: string, newValue: string): Promise<Category> {
    return await apiService.patch<Category>(`/categories/${id}/rename`, { newValue });
  }
}

export const categoryService = new CategoryService();
